"use client";

import { useState } from "react";

import { usePrefersDark } from "@/lib/usePrefersDark";

export type ObservedHeatRow = {
  kind: "observed_heat";
  year: number;
  metric: "hot_days" | "heat_waves" | "warm_nights";
  count: number;
};

export type ProjectedHeatRow = {
  kind: "projected_heat";
  metric: "hot_days" | "heat_waves" | "heat_wave_length" | "warm_nights";
  label: string;
  unit: string;
  source_table: string;
  historical_adjusted: number;
  value_2050s_p10: number;
  value_2050s_median: number;
  value_2050s_p90: number;
  value_2080s_p10: number;
  value_2080s_median: number;
  value_2080s_p90: number;
};

/**
 * Narrowed projected-row type for this chart. The component is
 * hot-days specific — its baseline label, aria-label, and observed
 * filter all assume `metric: "hot_days"`. Narrowing here turns a
 * mis-pass at the call site into a compile error instead of a
 * silent labelling bug.
 */
export type HotDaysProjection = ProjectedHeatRow & { metric: "hot_days" };

type Props = {
  observed: ObservedHeatRow[];
  projected: HotDaysProjection;
  height?: number;
};

const VIEW_W = 1000;
const PAD_TOP = 40;
const PAD_BOTTOM = 56;
const PAD_LEFT = 16;
const PAD_RIGHT = 168;

/**
 * Visual-first hot-days chart: bars are observed annual hot-days at YYC
 * (1990-present, ArcGIS Layer 5). Bars at or above the report's 2050s
 * lower bound (P10) render in alarm coral — "this year already had as
 * many hot days as the report's low estimate for the 2050s." Reference
 * lines mark the 1981–2010 baseline (where Calgary's climate used to
 * sit), the 2050s low end (the bar-coloring threshold), and the 2080s
 * median (where the central projection lands). All numeric values are
 * read from the artefact at render time, so a future data refresh
 * (e.g. a new Climate Projections edition) flows through automatically.
 * Y-axis numeric ticks are removed; only the three thresholds are
 * labelled inline at the right edge.
 */
export function ClimateHotDaysChart({
  observed,
  projected,
  height = 380,
}: Props) {
  const isDark = usePrefersDark();
  const [hover, setHover] = useState<string | null>(null);

  const obs = observed
    .filter((r) => r.metric === "hot_days")
    .sort((a, b) => a.year - b.year);

  // Y-domain derives from values actually drawn: observed bars and
  // the three threshold lines. The previous version included
  // `value_2080s_p90` so bands sized correctly, but the bands are no
  // longer drawn — keeping P90 in the domain just compressed observed
  // bars without visual payoff.
  const allValues = [
    ...obs.map((r) => r.count),
    projected.historical_adjusted,
    projected.value_2050s_p10,
    projected.value_2080s_median,
    0,
  ];
  const yMin = 0;
  const yMax = Math.ceil(Math.max(...allValues) / 10) * 10 + 10;

  const VIEW_H = height;
  const plotTop = PAD_TOP;
  const plotBottom = VIEW_H - PAD_BOTTOM;
  const plotLeft = PAD_LEFT;
  const plotRight = VIEW_W - PAD_RIGHT;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  const yToPx = (v: number) =>
    plotBottom - ((v - yMin) / (yMax - yMin)) * plotH;

  const yearSpan = obs.length > 0 ? obs[obs.length - 1].year - obs[0].year : 1;
  const barSlotW = plotW / Math.max(yearSpan + 1, 1);
  const barW = Math.max(barSlotW * 0.72, 2);
  const xForYear = (y: number) =>
    obs.length === 0
      ? plotLeft
      : plotLeft +
        ((y - obs[0].year) / Math.max(yearSpan, 1)) * (plotW - barSlotW) +
        barSlotW / 2;

  const ALARM = "var(--coral)";
  const NEUTRAL = isDark ? "rgba(213,213,208,0.42)" : "rgba(58,58,55,0.32)";
  const TODAY_LINE = "var(--coral)";
  const GHOST_LINE = "var(--ink-3)";

  // Alarm threshold for bar coloring: 2050s P10. Years already at or
  // above this match the report's *lower-bound* estimate for the
  // 2050s — "we're already living the floor of the projected future."
  const alarmLevel = projected.value_2050s_p10;

  type Threshold = {
    key: string;
    value: number;
    label: string;
    sub: string;
    style: "alarm" | "ghost" | "baseline";
  };
  // Format threshold values to one decimal so a future artefact
  // refresh that surfaces a float-rounding artefact (e.g. 15.19999) is
  // displayed cleanly. Matches the rainfall chart's threshold labels.
  const fmt = (v: number) => v.toFixed(1);
  const thresholds: Threshold[] = [
    {
      key: "baseline",
      value: projected.historical_adjusted,
      label: fmt(projected.historical_adjusted),
      sub: "1981–2010 baseline",
      style: "baseline",
    },
    {
      key: "alarm",
      value: alarmLevel,
      label: fmt(alarmLevel),
      sub: "2050s low end",
      style: "alarm",
    },
    {
      key: "future",
      value: projected.value_2080s_median,
      label: fmt(projected.value_2080s_median),
      sub: "2080s typical",
      style: "ghost",
    },
  ];

  return (
    <div className="cs-chart">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="w-full h-auto"
        role="img"
        aria-label={`Calgary hot days per year (max ≥29°C) at YYC, observed ${obs[0]?.year ?? ""}-${obs.at(-1)?.year ?? ""}. Bars at or above ${fmt(alarmLevel)} days are highlighted as already at or above the report's 2050s lower-bound projection. The 2080s median projection is ${fmt(projected.value_2080s_median)} days per year.`}
      >
        {/* Reference lines (drawn behind bars). Solid coral for the
            alarm line that bars are coloured against; dotted ghosts for
            the surrounding context (1981-2010 baseline below, 2080s
            median above). */}
        {thresholds.map((t) => {
          const y = yToPx(t.value);
          const isHover = hover === `th-${t.key}`;
          const isAlarm = t.style === "alarm";
          const stroke = isAlarm ? TODAY_LINE : GHOST_LINE;
          const dasharray = isAlarm ? undefined : "3 5";
          const opacity =
            t.style === "baseline" ? 0.55 : isHover ? 1 : isAlarm ? 1 : 0.7;
          return (
            <g
              key={t.key}
              tabIndex={0}
              role="img"
              aria-label={`${t.sub}: ${t.label} hot days per year`}
              onFocus={() => setHover(`th-${t.key}`)}
              onBlur={() => setHover(null)}
              onMouseEnter={() => setHover(`th-${t.key}`)}
              onMouseLeave={() => setHover(null)}
              style={{ outline: "none" }}
            >
              {isHover ? (
                <rect
                  x={plotLeft}
                  y={y - 4}
                  width={plotRight - plotLeft}
                  height={8}
                  fill={stroke}
                  opacity={0.18}
                  pointerEvents="none"
                />
              ) : null}
              <line
                x1={plotLeft}
                x2={plotRight}
                y1={y}
                y2={y}
                stroke={stroke}
                strokeWidth={isAlarm ? (isHover ? 2.4 : 1.8) : isHover ? 1.6 : 1}
                strokeDasharray={dasharray}
                opacity={opacity}
              />
              <text
                x={plotRight + 8}
                y={y}
                fontSize={11}
                fontFamily="var(--font-feature)"
                fontWeight={isAlarm ? 700 : 600}
                fill={isAlarm ? TODAY_LINE : "var(--ink-2)"}
                dominantBaseline="central"
              >
                {t.label}
              </text>
              <text
                x={plotRight + 8}
                y={y + 13}
                fontSize={9.5}
                fontFamily="var(--font-recursive)"
                fill="var(--ink-3)"
                dominantBaseline="central"
              >
                {t.sub}
              </text>
            </g>
          );
        })}

        {/* Observed bars. Each bar is a keyboard focus target so non-
            mouse readers step through the series via Tab and read the
            same year/value as the hover tooltip. */}
        {obs.map((r) => {
          const x = xForYear(r.year);
          const y = yToPx(r.count);
          const h = plotBottom - y;
          const isHover = hover === `obs-${r.year}`;
          const crosses = r.count >= alarmLevel;
          const fill = crosses ? ALARM : NEUTRAL;
          return (
            <g
              key={r.year}
              tabIndex={0}
              role="img"
              aria-label={`${r.year}: ${r.count} hot days at YYC${crosses ? " — at or above the 2050s lower-bound projection" : ""}`}
              onMouseEnter={() => setHover(`obs-${r.year}`)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(`obs-${r.year}`)}
              onBlur={() => setHover(null)}
              style={{ outline: "none" }}
            >
              <rect
                x={x - barW / 2}
                y={y}
                width={barW}
                height={Math.max(h, 0.5)}
                fill={fill}
                opacity={isHover ? 1 : 0.9}
                rx={1}
              />
              {isHover ? (
                <rect
                  x={x - barW / 2 - 2}
                  y={y - 2}
                  width={barW + 4}
                  height={Math.max(h, 0.5) + 4}
                  fill="none"
                  stroke="var(--ink)"
                  strokeWidth={1.5}
                  rx={2}
                  pointerEvents="none"
                />
              ) : null}
              {isHover ? (
                (() => {
                  const TIP_W = 130;
                  const tipLeft = Math.min(
                    Math.max(x - TIP_W / 2, 4),
                    VIEW_W - TIP_W - 4,
                  );
                  const tipMid = tipLeft + TIP_W / 2;
                  return (
                    <>
                      <rect
                        x={tipLeft}
                        y={y - 30}
                        width={TIP_W}
                        height={22}
                        rx={3}
                        fill="var(--bg)"
                        stroke="var(--rule)"
                        strokeWidth={0.6}
                        opacity={0.96}
                      />
                      <text
                        x={tipMid}
                        y={y - 16}
                        fontSize={11}
                        fontFamily="var(--font-feature)"
                        fontWeight={700}
                        fill="var(--ink)"
                        textAnchor="middle"
                      >
                        {r.year}: {r.count} hot days
                      </text>
                    </>
                  );
                })()
              ) : null}
            </g>
          );
        })}

        {/* Right-edge unit caption sits above the threshold labels. */}
        <text
          x={plotRight + 8}
          y={plotTop - 18}
          fontSize={10}
          fontFamily="var(--font-recursive)"
          fill="var(--ink-3)"
        >
          hot days/yr
        </text>

        {/* X-axis: first/last observed year labels and a subtitle. */}
        {obs.length > 0 ? (
          <>
            <text
              x={xForYear(obs[0].year)}
              y={plotBottom + 16}
              fontSize={11}
              fontFamily="var(--font-recursive)"
              fill="var(--ink-3)"
              textAnchor="start"
            >
              {obs[0].year}
            </text>
            <text
              x={xForYear(obs[obs.length - 1].year)}
              y={plotBottom + 16}
              fontSize={11}
              fontFamily="var(--font-recursive)"
              fill="var(--ink-3)"
              textAnchor="end"
            >
              {obs[obs.length - 1].year}
            </text>
            <text
              x={(xForYear(obs[0].year) + xForYear(obs[obs.length - 1].year)) / 2}
              y={plotBottom + 32}
              fontSize={10}
              fontFamily="var(--font-recursive)"
              fill="var(--ink-3)"
              textAnchor="middle"
            >
              days at YYC with max ≥29°C · ArcGIS Layer 5 · projections {projected.source_table}
            </text>
          </>
        ) : null}
      </svg>
    </div>
  );
}
